import { useState, useEffect, useContext } from "react";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import {
  StudentsActionLabels,
  StudentsColumns,
  IStudent,
  orderBySet,
  orderSet,
  selectOrder,
  selectOrderBy,
  selectSelectAll,
  selectStudents,
  studentsLoaded,
  selectAllSet,
} from "features/Students";
import { useAppDispatch, useAppSelector } from "app/hooks";
import TableBody from "./TableBody";
import DataNotFound from "components/DataNotFound";
import { DeskIcon } from "components/SVGs";
import { useTranslation } from "react-i18next";
import TableSkeleton from "../../common/Skeletons/Table";
import TableHead from "../../common/TableHead";
import { FireBaseContext } from "contexts/FireBase";

interface IStudentTable {
  allList: IStudent[];
  pageList: IStudent[];
  filteredList: IStudent[];
}
export default function StudentsTable({
  pageList,
  allList,
  filteredList,
}: IStudentTable) {
  const { logEvent } = useContext(FireBaseContext);
  const dispatch = useAppDispatch();
  const order = useAppSelector((state) => selectOrder(state));
  const orderBy = useAppSelector((state) => selectOrderBy(state));
  const selectAll = useAppSelector(selectSelectAll);
  const studentList = useAppSelector(selectStudents);
  const [isCompMounted, setCompMounted] = useState(false);
  useEffect(() => {
    const handle = setTimeout(() => setCompMounted(true), 200);
    return () => {
      setCompMounted(false);
      clearTimeout(handle);
      dispatch(selectAllSet(false));
    };
  }, []);

  const { t } = useTranslation();
  const columns = Object.keys(StudentsColumns) as StudentsColumns[];

  const handleRequestSort = (
    event: React.MouseEvent<unknown, MouseEvent>,
    property: StudentsColumns
  ) => {
    const isAsc = orderBy === property && order === "asc";
    dispatch(orderSet(isAsc ? "desc" : "asc"));
    dispatch(orderBySet(property));
    const sortType = {
      Student: "By Name",
      StudentId: "By ID",
    };
    logEvent("ClickOnSortStudent", {
      Sort_Type: sortType[property as keyof typeof sortType],
    });
  };

  const handleSelectAll = () => {
    dispatch(selectAllSet(!selectAll));
    toggleSelectedStudents(!selectAll);
    logEvent("ClickOnSelectAllStudents");
  };
  return !pageList.length && isCompMounted ? (
    <DataNotFound
      text={allList?.length ? t("No data") : t("No students exists")}
      icon={<DeskIcon sx={{ fontSize: "120px" }} />}
    />
  ) : isCompMounted ? (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 700 }} aria-label="Students List">
        <TableHead<StudentsColumns>
          data={columns}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          actionLabels={Object.keys(StudentsActionLabels) as string[]}
          counters={{ Student: filteredList.length }}
          withCheckBox
          selectAll={selectAll}
          setSelectAll={handleSelectAll}
        />
        <TableBody list={pageList} withCheckBox />
      </Table>
    </TableContainer>
  ) : (
    <TableSkeleton />
  );

  function toggleSelectedStudents(value: boolean) {
    const updateStudents = structuredClone(studentList).reduce<IStudent[]>(
      (list, stu) => {
        list.push({ ...stu, selected: value });
        return list;
      },
      []
    );
    dispatch(studentsLoaded(updateStudents));
  }
}
